import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import { useTaskStore } from "@/store/task";
import { dobToDateInputValue } from "@/utils/intakeHelpers";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { IntakeRenderer } from "@/components/intake/IntakeRenderer";

/**
 * Landing + schema-driven pediatric intake. Remount IntakeRenderer when seeded demographics change.
 */
export function PediatricHistoryPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const patient = useProfileStore((s) => s.currentPatient);
  const demographics = useDemographicsStore((s) => s.demographics);
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id ?? 0);

  const patientKey =
    patient?.patientKey != null && String(patient.patientKey).trim()
      ? String(patient.patientKey)
      : patient?.patientID != null
        ? String(patient.patientID)
        : "";

  const intakeKey = useMemo(() => {
    const dob = demographics?.dob ?? patient?.dob ?? "";
    return `${caseHistoryId}-${patientKey}-${dobToDateInputValue(typeof dob === "string" ? dob : String(dob))}`;
  }, [caseHistoryId, patientKey, demographics?.dob, patient?.dob]);

  const goBack = () => {
    if (showForm) {
      setShowForm(false);
      return;
    }
    navigate("/complaint/financial-responsibility-insurance");
  };

  if (showForm) {
    return (
      <div className="wizard-page w-full">
        <IntakeRenderer
          key={intakeKey}
          onExitFromFirstPage={() => setShowForm(false)}
        />
      </div>
    );
  }

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content px-4 pt-6 pb-4">
        <div className="mx-auto max-w-lg text-center">
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
            Tell us about your child
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Please complete this short health history before your child&apos;s visit.
          </p>
          <div className="mx-auto mt-6 grid max-w-md grid-cols-2 gap-3 sm:gap-4">
            <img
              src="/infantpeds.png"
              alt="Provider gently supporting an infant during a pediatric exam"
              className="w-full rounded-lg object-cover shadow-sm"
            />
            <img
              src="/olderchildpeds.png"
              alt="Provider and young patient sharing a high-five in the clinic"
              className="w-full rounded-lg object-cover shadow-sm"
            />
          </div>
          <Button
            type="button"
            className="mt-8 w-full max-w-full sm:w-auto sm:min-w-[240px]"
            onClick={() => setShowForm(true)}
          >
            Tell Us About Your Child
          </Button>
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>
          Back
        </Button>
      </WizardActionsBar>
    </div>
  );
}
